import React, { useState, useEffect } from 'react';
import { X, Upload, GripVertical, Trash2 } from 'lucide-react';
import { CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET, CLOUDINARY_PORTFOLIO_FOLDER } from '../config';

const PortfolioEditForm = ({ project, onSave, onClose, submitting }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    long_description: '',
    category: '',
    type: '',
    materials: '',
    gallery: [],
    ...project,
  });

  const [imageFiles, setImageFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploading, setUploading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    if (project) {
      setFormData({
        title: '',
        description: '',
        long_description: '',
        category: '',
        type: '',
        materials: '',
        gallery: [],
        ...project,
      });
    }
  }, [project]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const validTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp', 'image/avif'];
    
    const validFiles = files.filter(file => {
      if (!validTypes.includes(file.type)) {
        alert(`Arquivo ${file.name} não é uma imagem válida. Use PNG, JPG, JPEG, WebP ou AVIF.`);
        return false;
      }
      return true;
    });

    if (validFiles.length + imageFiles.length + formData.gallery.length > 10) {
      alert('Máximo de 10 imagens permitidas.');
      return;
    }

    setImageFiles(prev => [...prev, ...validFiles]);
  };

  const removeImageFile = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index)
    }));
  };

  // Funções de drag and drop para reordenar imagens existentes
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newGallery = [...formData.gallery];
    const draggedItem = newGallery[draggedIndex];
    
    // Remove o item da posição original
    newGallery.splice(draggedIndex, 1);
    
    // Insere na nova posição
    newGallery.splice(dropIndex, 0, draggedItem);
    
    setFormData(prev => ({
      ...prev,
      gallery: newGallery
    }));
    
    setDraggedIndex(null);
  };

  const uploadImageToCloudinary = async (file, index) => {
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    if (CLOUDINARY_PORTFOLIO_FOLDER) uploadFormData.append('folder', CLOUDINARY_PORTFOLIO_FOLDER);
    
    const timestamp = Date.now();
    const cleanTitle = (formData.title || 'portfolio').toLowerCase().replace(/[^a-z0-9-]+/g, '-');
    uploadFormData.append('public_id', `${cleanTitle}-${timestamp}-${index}`);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        {
          method: 'POST',
          body: uploadFormData
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data?.error?.message || 'Erro no upload da imagem');
      }

      return data.secure_url || data.url;
    } catch (error) {
      console.error('Erro no upload para Cloudinary:', error);
      throw error;
    }
  };

  const uploadImages = async () => {
    if (imageFiles.length === 0) return [];

    setUploading(true);
    const uploadedUrls = [];

    try {
      if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
        throw new Error('Cloudinary não está configurado. Configure REACT_APP_CLOUDINARY_CLOUD_NAME e REACT_APP_CLOUDINARY_UPLOAD_PRESET.');
      }

      for (let i = 0; i < imageFiles.length; i++) {
        const file = imageFiles[i];
        setUploadProgress(prev => ({
          ...prev,
          [i]: 20
        }));
        const imageUrl = await uploadImageToCloudinary(file, i);
        uploadedUrls.push(imageUrl);
        setUploadProgress(prev => ({
          ...prev,
          [i]: 100
        }));
      }
    } finally {
      setUploading(false);
      setUploadProgress({});
    }
    return uploadedUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      alert('Título é obrigatório');
      return;
    }

    if (!formData.description.trim()) {
      alert('Descrição resumida é obrigatória');
      return;
    }

    if (!formData.category) {
      alert('Categoria é obrigatória');
      return;
    }

    try {
      const uploadedImages = await uploadImages();
      const finalGallery = [...formData.gallery, ...uploadedImages];

      if (finalGallery.length === 0) {
        alert('Pelo menos uma imagem é obrigatória');
        return;
      }

      const projectData = {
        ...formData,
        gallery: finalGallery
      };

      await onSave(project.id, projectData);
      
      // Reset form
      setImageFiles([]);
      setUploadProgress({});
      onClose();
    } catch (error) {
      console.error('Erro ao salvar projeto:', error);
      alert('Erro ao salvar projeto: ' + error.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-[#2C5F5D]">Editar Projeto</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Título do Projeto *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441] focus:border-transparent"
                placeholder="Ex: Sala de Estar Moderna"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441] focus:border-transparent"
                >
                  <option value="">Selecione</option>
                  <option value="Residencial">Residencial</option>
                  <option value="Comercial">Comercial</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Material
                </label>
                <input
                  type="text"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441] focus:border-transparent"
                  placeholder="Ex: Piso Vinílico"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição Resumida *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows="2"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441] focus:border-transparent"
              placeholder="Descrição breve que aparecerá na listagem..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição Detalhada
            </label>
            <textarea
              name="long_description"
              value={formData.long_description}
              onChange={handleInputChange}
              rows="4"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441] focus:border-transparent"
              placeholder="Descrição completa do projeto..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Materiais Utilizados
            </label>
            <input
              type="text"
              name="materials"
              value={formData.materials}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441] focus:border-transparent"
              placeholder="Ex: Piso Vinílico LVT Eucafloor"
            />
          </div>

          {/* Imagens Existentes */}
          {formData.gallery && formData.gallery.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Galeria Atual (arraste para reordenar)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {formData.gallery.map((imageUrl, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    className={`relative group border-2 border-dashed border-gray-300 rounded-lg overflow-hidden cursor-move hover:border-[#D67441] transition-colors ${
                      draggedIndex === index ? 'opacity-50' : ''
                    }`}
                  >
                    <img
                      src={imageUrl}
                      alt={`Imagem ${index + 1}`}
                      className="w-full h-24 object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                      <GripVertical size={16} className="text-white" />
                      <button
                        type="button"
                        onClick={() => removeExistingImage(index)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Adicionar Mais Imagens */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Adicionar Mais Imagens
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-[#D67441] transition-colors">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
                id="portfolio-images"
                disabled={uploading}
              />
              <label
                htmlFor="portfolio-images"
                className="cursor-pointer flex flex-col items-center space-y-2"
              >
                <Upload size={32} className="text-gray-400" />
                <span className="text-sm text-gray-600">
                  Clique para adicionar imagens ou arraste aqui
                </span>
                <span className="text-xs text-gray-500">
                  PNG, JPG, WebP, AVIF (máx. 10 imagens total)
                </span>
              </label>
            </div>

            {/* Preview das novas imagens */}
            {imageFiles.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                {imageFiles.map((file, index) => (
                  <div key={index} className="relative">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Nova imagem ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeImageFile(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    >
                      <X size={14} />
                    </button>
                    {uploadProgress[index] && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gray-200 rounded-b-lg">
                        <div
                          className="bg-[#D67441] h-1 rounded-b-lg transition-all duration-300"
                          style={{ width: `${uploadProgress[index]}%` }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting || uploading}
              className="px-6 py-2 bg-[#D67441] text-white rounded-lg hover:bg-[#C5633A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting || uploading ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PortfolioEditForm; 